import {
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tooltip,
} from "@bfc-software/pallet";
import { useCurrentLocalization } from "@bfc-software/bfc-web-ui/i18n";

import type { TaskRow } from "../-data/task-model";
import type { ActivatedTask, Task } from "../-data/types";

import {
  AutoTags,
  NotReleasedMark,
  Progress,
  TaskIdentity,
  TaskStatusTag,
} from "./task-bits";

/**
 * The task table, used by both tabs.
 *
 * One component with an optional plan column rather than two tables: the columns
 * either side of it are identical, and the version that diverges is the one where
 * a fix to the progress cell lands in the All tasks table and not in the plan's.
 *
 * Also one component across the two task worlds, now that the plans tab can
 * expand a card onto a plan's unactivated tasks (§0): the Selector, Progress and
 * Status cells each switch on `row.task.kind`/`row.progress.kind` rather than
 * the table splitting into an activated version and a planned one, for the same
 * reason the plan column is optional rather than a second table - the version
 * that diverges is the one where a fix lands in one world and not the other.
 *
 * The checkbox column is optional for the same reason as `showPlan`: an
 * unactivated plan's table has no row that could ever be assigned, so it simply
 * passes no `selection` rather than drawing a column of disabled boxes.
 */
export function TaskTable({
  rows,
  ariaLabel,
  showPlan = false,
  onSelect,
  selection,
}: {
  rows: TaskRow[];
  ariaLabel: string;
  /** The All tasks tab names each task's plan; inside a card it is redundant. */
  showPlan?: boolean;
  /**
   * The whole row's task, not its id.
   *
   * The detail drawer is addressed by plan *and* task number, and only one of
   * those is the id a row was clicked by - handing back the id alone would make
   * every caller look the task up again in the list it had just handed us.
   */
  onSelect: (task: Task) => void;
  /**
   * Row selection for assignment. Absent: no checkbox column at all (an unactivated plan's
   * table, where no row could ever be assignable).
   * No header select-all: a page is not the set of rows a manager means (paging), so each row is ticked by hand.
   */
  selection?: {
    isSelected: (task: ActivatedTask) => boolean;
    isSelectable: (task: Task) => boolean;
    onToggle: (task: Task) => void;
  };
}) {
  const [t] = useCurrentLocalization();
  return (
    <Table aria-label={ariaLabel} isResizable={false}>
      <TableHead>
        {selection ? (
          <TableHeaderCell
            id="select"
            textValue={t("tasks.assign.selectColumn")}
            display={{ width: 48 }}
          >
            {/* Empty on purpose: the header select-all is out (paging). */}
          </TableHeaderCell>
        ) : (
          <></>
        )}
        <TableHeaderCell isRowHeader={true}>
          {t("tasks.table.taskRoute")}
        </TableHeaderCell>
        {showPlan ? (
          <TableHeaderCell>{t("tasks.table.plan")}</TableHeaderCell>
        ) : (
          <></>
        )}
        <TableHeaderCell>{t("tasks.table.selector")}</TableHeaderCell>
        <TableHeaderCell>{t("tasks.table.zone")}</TableHeaderCell>
        <TableHeaderCell align="right">
          {t("tasks.table.lines")}
        </TableHeaderCell>
        <TableHeaderCell display={{ width: 200 }}>
          {t("tasks.table.customer")}
        </TableHeaderCell>
        <TableHeaderCell>{t("tasks.table.progress")}</TableHeaderCell>
        <TableHeaderCell>{t("tasks.table.status")}</TableHeaderCell>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow
            key={row.task.id}
            data-test-role="task-row"
            onAction={() => onSelect(row.task)}
          >
            {selection ? (
              <TableCell display={{ padding: "small" }}>
                {/* Narrows `taskUlid`; a planned row has no ULID to select. */}
                {row.task.kind === "activated" ? (
                  <Checkbox
                    name="task"
                    value={row.task.taskUlid}
                    aria-label={t("tasks.assign.selectRow", {
                      id: row.task.id,
                    })}
                    isSelected={selection.isSelected(row.task)}
                    isDisabled={!selection.isSelectable(row.task)}
                    onChange={() => selection.onToggle(row.task)}
                  />
                ) : (
                  <></>
                )}
              </TableCell>
            ) : (
              <></>
            )}
            <TableCell>
              <TaskIdentity
                taskId={row.task.id}
                routeCodes={row.task.routeCodes}
              />
            </TableCell>
            {showPlan ? (
              <TableCell>
                {/* The plan's warehouse-scoped number, not the ULID the row is
                    keyed by - a manager reads out "PLAN-3120". */}
                <span className="font-mono text-size-13 font-strong text-on-surface-variant">
                  {row.task.planName}
                </span>
              </TableCell>
            ) : (
              <></>
            )}
            <TableCell>
              {/*
                Blank when nobody holds the task (SR-342 item 10): an empty cell
                is the clearer signal, and the Status column now says "Open" for
                exactly these rows. A planned task still renders the shared
                em-dash - it is held by nobody rather than unassigned.
              */}
              <span className="text-on-surface-variant text-size-13">
                {row.task.kind === "activated"
                  ? (row.task.selectorName ?? "")
                  : t("tasks.row.none")}
              </span>
            </TableCell>
            <TableCell>
              {/*
                Empty is normal, not missing: a plan created before the service
                began stamping zone_ext_id onto pick snapshots carries none and
                never backfills (§4). So this draws nothing at all - no em-dash,
                unlike the selector column above, where "nobody holds this" is a
                fact worth stating. "No zone recorded" is an artifact of when
                the plan was made, and saying so on every legacy row would be
                noise.
              */}
              {row.task.zoneNames.length > 0 ? (
                <AutoTags labels={row.task.zoneNames} />
              ) : (
                <></>
              )}
            </TableCell>
            <TableCell align="right">
              <span className="text-on-surface-variant text-size-13">
                {row.task.lineCount}
              </span>
            </TableCell>
            <TableCell>
              {/*
                Empty draws nothing, for the zone cell's reason above: a pick
                whose stop never resolved to a customer has no snapshot, which
                is a fact about the plan and not worth stating on every row.
                Text rather than tags - names are long and a task at several
                stops would crowd the row - cut at the cell edge with the whole
                list in the tooltip.
              */}
              {row.task.customerNames.length > 0 ? (
                <Tooltip
                  content={row.task.customerNames.join(", ")}
                  trigger="span"
                  display={{ overflow: "ellipsis" }}
                >
                  <span className="block truncate text-on-surface-variant text-size-13">
                    {row.task.customerNames.join(", ")}
                  </span>
                </Tooltip>
              ) : (
                <></>
              )}
            </TableCell>
            <TableCell>
              {row.progress.kind === "known" ? (
                <Progress
                  done={row.progress.picksDone}
                  total={row.progress.picksTotal}
                  percent={row.progress.percent}
                />
              ) : (
                <span className="text-on-surface-variant text-size-13">
                  {t("tasks.row.none")}
                </span>
              )}
            </TableCell>
            <TableCell>
              {row.task.kind === "activated" ? (
                <TaskStatusTag status={row.task.status} />
              ) : (
                <NotReleasedMark />
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
